import React from "react";
import { PageHeaderLink } from "component/shared/page-header";
import * as Mui from "@mui/material";
import { withStyles } from "tss-react/mui";
import type { WithStyles } from "type/tss";
import { Alert } from "@mui/material";
import { connect } from "react-redux";
import { bindActionCreators, Dispatch } from "redux";
import { DashboardLayout } from "component/layout/dashboard";
import { FullPageCard } from "component/shared/full-page-card";
import { Switch } from "component/shared/switch";
import { styles } from "./style";
import * as env from "shared/env";
import { SettingsStickyFooter } from "component/shared/settings-sticky-footer";
import { MultiRadioField } from "component/shared/multi-radio-field";
import { FeatherIcon } from "component/shared/feather-icon";
import { UnsavedChangesPrompt } from "component/shared/unsaved-changes-prompt";
import { PageProps } from "shared/page-props";
import * as Router from "react-router-dom";
import * as Icons from "react-feather";
import { User } from "model/user";
import { saveUser } from "redux/slice/authentication";
import { fetchPages } from "redux/slice/pages";
import { getPages } from "redux/selector";
import { RootState } from "redux/store";
import { OpenHouseDisplayType } from "type/open-house-display";
import { OpenHouseSearchType } from "type/open-house-search";
import { PageType } from "type/page-type";

const isRefresh = env.refreshDesignsDev;

interface Props
	extends PageProps,
		Router.RouteComponentProps,
		WithStyles<typeof styles>,
		ReturnType<typeof mapStateToProps>,
		ReturnType<typeof mapDispatchToProps> {
	user: User;
}

interface State {
	displayEnabled: boolean;
	displayLimitToOwn: boolean;
	searchEnabled: boolean;
	searchLimitToOwn: boolean;
	saving: boolean;
	snackbarOpen: boolean;
}

const mapStateToProps = (state: RootState) => ({
	authLoading: state.authentication.loading,
	authError: state.authentication.error,
	pages: getPages(state),
});

const mapDispatchToProps = (dispatch: Dispatch) =>
	bindActionCreators({ saveUser, fetchPages }, dispatch);

function stateFromUser(user: User): Pick<State, "displayEnabled" | "displayLimitToOwn" | "searchEnabled" | "searchLimitToOwn"> {
	const displayId = user.openHouseDisplayType?.id ?? 0;
	const searchId = user.openHouseSearchType?.id ?? 0;
	return {
		displayEnabled: displayId !== OpenHouseDisplayType.OFF.id,
		displayLimitToOwn: displayId === OpenHouseDisplayType.OWN_LISTINGS.id,
		searchEnabled: searchId !== OpenHouseSearchType.OFF.id,
		searchLimitToOwn: searchId === OpenHouseSearchType.OWN_LISTINGS.id,
	};
}

class Component extends React.Component<Props, State> {
	constructor(props: Props) {
		super(props);
		this.state = {
			...stateFromUser(props.user),
			saving: false,
			snackbarOpen: false,
		};
	}

	public componentDidMount() {
		if (isRefresh) {
			this.props.fetchPages();
		}
	}

	public componentDidUpdate(prevProps: Props) {
		const { authLoading, authError } = this.props;
		if (prevProps.authLoading && !authLoading && !authError && this.state.saving) {
			this.setState({ saving: false });
		}
	}

	private isDirty(): boolean {
		const orig = stateFromUser(this.props.user);
		const { displayEnabled, displayLimitToOwn, searchEnabled, searchLimitToOwn } = this.state;
		return (
			displayEnabled !== orig.displayEnabled ||
			displayLimitToOwn !== orig.displayLimitToOwn ||
			searchEnabled !== orig.searchEnabled ||
			searchLimitToOwn !== orig.searchLimitToOwn
		);
	}

	private handleSave = () => {
		const { saveUser, user } = this.props;
		const { displayEnabled, displayLimitToOwn, searchEnabled, searchLimitToOwn } = this.state;

		const openHouseDisplayType = !displayEnabled
			? OpenHouseDisplayType.OFF
			: displayLimitToOwn
			? OpenHouseDisplayType.OWN_LISTINGS
			: OpenHouseDisplayType.ALL_LISTINGS;

		const openHouseSearchType = !searchEnabled
			? OpenHouseSearchType.OFF
			: searchLimitToOwn
			? OpenHouseSearchType.OWN_LISTINGS
			: OpenHouseSearchType.ANY_LISTINGS;

		saveUser({
			user: {
				...user,
				openHouseDisplayType,
				openHouseSearchType,
			},
		});
		this.setState({ saving: true, snackbarOpen: true });
	};

	private handleCancel = () => {
		this.setState({ ...stateFromUser(this.props.user) });
	};

	public render() {
		const { authError, authLoading, user, pages, classes = {} } = this.props;
		const { displayEnabled, displayLimitToOwn, searchEnabled, searchLimitToOwn, saving, snackbarOpen } = this.state;
		const dirty = this.isDirty();

		if (isRefresh) {
			const listingsPage =
				pages?.find(p => p.key === 'LISTING_SEARCH_FORM')
				?? pages?.find(p => p.type.id === PageType.LISTINGS.id);
			const listingsPageUrl = listingsPage ? user.baseUrl + listingsPage.path : undefined;
			const title = "Open Houses";

			return (
				<DashboardLayout
					permitted={user && (user.permissions.listingSearch || user.permissions.listingMapSearch)}
					title={title}
					header={
						<Mui.Grid container direction="row" alignItems="center" justifyContent="space-between">
							<Mui.Typography variant="h1">{title}</Mui.Typography>
							{listingsPageUrl && (
								<PageHeaderLink href={listingsPageUrl}>View Property Search</PageHeaderLink>
							)}
						</Mui.Grid>
					}
				>
					<Mui.Typography variant="body2" className={classes.pageSubtitle}>
						Manage the visibility of open house information on your website.
					</Mui.Typography>

					<Mui.Box mb={2}>
						<FullPageCard>
							<Mui.Grid container direction="column" spacing={2}>
								<Mui.Grid item>
									<Mui.Typography className={classes.sectionHeading}>Listing Detail Pages</Mui.Typography>
								</Mui.Grid>
								<Mui.Grid item>
									<Switch
										checked={displayEnabled}
										onChange={(value) => this.setState({ displayEnabled: value })}
										label="Show open house information on listing detail pages"
										description="When enabled, available open house information will appear on listing detail pages. When disabled, open house information will be hidden."
									/>
									{displayEnabled && (
										<Mui.Box style={{ marginTop: 24, marginLeft: 58 }}>
											<Mui.FormControlLabel
												style={{ alignItems: "flex-start" }}
												control={
													<Mui.Checkbox
														style={{ paddingTop: 0 }}
														checked={displayLimitToOwn}
														onChange={(e) => this.setState({ displayLimitToOwn: e.target.checked })}
														color="secondary"
													/>
												}
												label={
													<Mui.Box>
														<Mui.Typography className={classes.labelTitle}>
															Limit to my featured listings only
														</Mui.Typography>
														<Mui.Typography className={classes.labelDescription}>
															Limit display to your featured listings only (listings where you or your brokerage represent the seller).
														</Mui.Typography>
													</Mui.Box>
												}
											/>
										</Mui.Box>
									)}
								</Mui.Grid>
							</Mui.Grid>
						</FullPageCard>
					</Mui.Box>

					<Mui.Box mb={2}>
						<FullPageCard>
							<Mui.Grid container direction="column" spacing={2}>
								<Mui.Grid item>
									<Mui.Typography className={classes.sectionHeading}>Search Results</Mui.Typography>
								</Mui.Grid>
								<Mui.Grid item>
									<Switch
										checked={searchEnabled}
										onChange={(value) => this.setState({ searchEnabled: value })}
										label="Allow leads to search for open houses"
										description="When enabled, visitors on your website will have the option to filter search results by open houses. When disabled, this option will be hidden."
									/>
									{searchEnabled && (
										<Mui.Box style={{ marginTop: 24, marginLeft: 58 }}>
											<Mui.FormControlLabel
												style={{ alignItems: "flex-start" }}
												control={
													<Mui.Checkbox
														style={{ paddingTop: 0 }}
														checked={searchLimitToOwn}
														onChange={(e) => this.setState({ searchLimitToOwn: e.target.checked })}
														color="secondary"
													/>
												}
												label={
													<Mui.Box>
														<Mui.Typography className={classes.labelTitle}>
															Limit to my featured listings only
														</Mui.Typography>
														<Mui.Typography className={classes.labelDescription}>
															Limit display to your featured listings only (listings where you or your brokerage represent the seller).
														</Mui.Typography>
													</Mui.Box>
												}
											/>
										</Mui.Box>
									)}
								</Mui.Grid>
							</Mui.Grid>
						</FullPageCard>
					</Mui.Box>

					<SettingsStickyFooter
						dirty={dirty}
						saving={saving}
						authError={authError}
						onSave={this.handleSave}
						onCancel={this.handleCancel}
					/>

					<Mui.Snackbar
						open={snackbarOpen}
						autoHideDuration={3000}
						onClose={() => this.setState({ snackbarOpen: false })}
						anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
					>
						<Alert onClose={() => this.setState({ snackbarOpen: false })} severity="success">
							Settings saved successfully!
						</Alert>
					</Mui.Snackbar>

					<UnsavedChangesPrompt isDirty={() => this.isDirty()} history={this.props.history} />
				</DashboardLayout>
			);
		}

		// Legacy UI
		const openHouseDisplayType = !displayEnabled
			? OpenHouseDisplayType.OFF
			: displayLimitToOwn ? OpenHouseDisplayType.OWN_LISTINGS : OpenHouseDisplayType.ALL_LISTINGS;
		const openHouseSearchType = !searchEnabled
			? OpenHouseSearchType.OFF
			: searchLimitToOwn ? OpenHouseSearchType.OWN_LISTINGS : OpenHouseSearchType.ANY_LISTINGS;
		const title = "Open House Settings";

		return (
			<DashboardLayout
				permitted={user && (user.permissions.listingSearch || user.permissions.listingMapSearch)}
				title={title}
				header={
					<Mui.Typography variant="h1">
						<FeatherIcon>
							<Icons.Settings />
						</FeatherIcon>
						{title}
					</Mui.Typography>
				}
			>
				<FullPageCard>
					<Mui.Grid container direction="column" spacing={3}>
						<Mui.Grid item>
							<MultiRadioField
								label="Open House Information Display"
								onChange={(value) => {
									const type = OpenHouseDisplayType.getByIdOrDefault(parseInt(value));
									this.setState({
										displayEnabled: type.id !== OpenHouseDisplayType.OFF.id,
										displayLimitToOwn: type.id === OpenHouseDisplayType.OWN_LISTINGS.id,
									});
								}}
								options={OpenHouseDisplayType.values().map((option) => ({
									label: option.label,
									value: option.id,
								}))}
								value={openHouseDisplayType.id}
							/>
						</Mui.Grid>
						<Mui.Grid item>
							<MultiRadioField
								label="Open House Search"
								onChange={(value) => {
									const type = OpenHouseSearchType.getByIdOrDefault(parseInt(value));
									this.setState({
										searchEnabled: type.id !== OpenHouseSearchType.OFF.id,
										searchLimitToOwn: type.id === OpenHouseSearchType.OWN_LISTINGS.id,
									});
								}}
								options={OpenHouseSearchType.values().map((option) => ({
									label: option.label,
									value: option.id,
								}))}
								value={openHouseSearchType.id}
							/>
						</Mui.Grid>
					</Mui.Grid>
					<Mui.Grid container spacing={1} style={{ marginTop: 24 }}>
						<Mui.Grid item>
							<Mui.Button
								color={"primary"}
								disabled={saving}
								onClick={this.handleSave}
								style={{ marginRight: 32 }}
								variant="contained"
							>
								Save Changes
							</Mui.Button>
							<Mui.Button
								onClick={this.handleCancel}
								variant="contained"
							>
								Cancel
							</Mui.Button>
						</Mui.Grid>
					</Mui.Grid>
					<Mui.Snackbar
						open={saving && !authLoading && !authError}
						message="Settings saved"
						autoHideDuration={6000}
						onClose={() => this.setState({ saving: false })}
						action={
							<Mui.IconButton
								size="small"
								aria-label="close"
								color="inherit"
								onClick={() => this.setState({ saving: false })}
							>
								<Icons.X fontSize="small" />
							</Mui.IconButton>
						}
					>
						<Alert severity="info">
							<Mui.Typography>Settings saved</Mui.Typography>
						</Alert>
					</Mui.Snackbar>
				</FullPageCard>
			</DashboardLayout>
		);
	}
}

export const SettingsOpenHousePage = Router.withRouter(
	withStyles(connect(mapStateToProps, mapDispatchToProps)(Component), styles)
);
